type StepConfig = {
  key: string;
  sheet?: string;
};

type SheetRow = Record<string, any>;

function normalize(value: unknown) {
  return String(value ?? "")
    .toLowerCase()
    .replace(/[’]/g, "'")
    .replace(/["']/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeFieldKey(value: unknown) {
  return String(value ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function isUnknown(value: unknown) {
  const v = normalize(value);
  return !v || v.includes("dont know") || v.includes("don't know") || v.includes("not sure");
}

function stripOuterQuotes(value: string) {
  return value.replace(/^"+|"+$/g, "").trim();
}

function cleanLiteral(value: string) {
  return stripOuterQuotes(String(value ?? "").replace(/[.,;:]$/, "").trim());
}

function toNumberIfNumeric(value: unknown) {
  const raw = String(value ?? "").trim();
  if (!raw) return null;
  const cleaned = raw.replace(/[$,\s]/g, "");
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
}

function rowColumns(row: SheetRow) {
  const values = Object.values(row ?? {});
  return {
    colA: stripOuterQuotes(String(values[0] ?? "")),
    colB: stripOuterQuotes(String(values[1] ?? "")),
    colC: stripOuterQuotes(String(values[2] ?? "")),
  };
}

function buildFieldAliasMap(steps: StepConfig[], lead: Record<string, any>) {
  const map: Record<string, string> = {};

  const remember = (alias: string, key: string) => {
    const normalized = normalizeFieldKey(alias);
    if (!normalized || !key) return;
    map[normalized] = key;
  };

  for (const key of Object.keys(lead ?? {})) {
    remember(key, key);
  }

  for (const step of steps) {
    if (!step?.key) continue;
    remember(step.key, step.key);
    remember(step.key.replace(/([A-Z])/g, " $1"), step.key);
  }

  // explicit aliases used by sheet text
  remember("desiredSystemType", "desiredSystemType");
  remember("desiredFueltype", "desiredFuelType");
  remember("desiredFuelType", "desiredFuelType");
  remember("desiredfuletype", "desiredFuelType");
  remember("currentACtonnage", "currentACtonnage");
  remember("acTonnage", "currentACtonnage");
  remember("tonnage", "currentACtonnage");
  remember("currentSystemAge", "currentSystemAge");
  remember("miniSplitrooms", "miniSplitRooms");
  remember("system Location", "systemlocation");

  return map;
}

function resolveFieldFromText(
  fieldText: string,
  aliasMap: Record<string, string>,
  contextStepKey?: string,
) {
  const token = normalizeFieldKey(fieldText);
  // Some sheets use "desiredSystemtype" text while clearly referring to fuel logic.
  if (token === "desiredsystemtype" && contextStepKey === "currentFuelType") {
    return "desiredFuelType";
  }
  return aliasMap[token];
}

function parseAssignments(instructionTail: string) {
  return instructionTail
    .split(/\s+\band\b\s+/i)
    .map((chunk) => chunk.trim())
    .map((chunk) => {
      const normalizedChunk = chunk.replace(/^then\s+/i, "").trim();
      const match = normalizedChunk.match(/^(.+?)\s*=\s*(.+)$/i);
      if (!match) return null;
      return {
        targetText: cleanLiteral(match[1]),
        sourceText: cleanLiteral(match[2]),
      };
    })
    .filter((item): item is { targetText: string; sourceText: string } => !!item);
}

export function applySheetInstructionRules(
  lead: Record<string, any>,
  data: Record<string, SheetRow[]>,
  steps: StepConfig[],
) {
  const nextLead: Record<string, any> = { ...lead };
  const aliasMap = buildFieldAliasMap(steps, nextLead);
  const appliedRules: string[] = [];
  const displayNotes: Record<string, string> = {};

  for (const step of steps) {
    if (!step.sheet) continue;
    const rows = data?.[step.sheet];
    if (!Array.isArray(rows) || rows.length === 0) continue;

    const selectedValue = nextLead[step.key];
    if (selectedValue === undefined || selectedValue === null || selectedValue === "") continue;

    // CSV parser already uses row 1 as header; rows here are data rows (sheet line 2+).
    const selectedRow = rows.find((row) => normalize(rowColumns(row).colA) === normalize(selectedValue));
    if (!selectedRow) continue;

    const { colB: instruction, colC: mappedValueRaw } = rowColumns(selectedRow);
    if (!instruction) continue;

    const mappedNumeric = toNumberIfNumeric(mappedValueRaw);

    // Pattern: "Assumed SEER rating" with numeric value in col C.
    // Example:
    // A: 5-10 years, B: Assumed SEER rating, C: 14
    if (/assumed\s+seer\s+rating/i.test(instruction) && mappedNumeric !== null) {
      nextLead.assumedSeerRating = mappedNumeric;
      appliedRules.push(`${step.key}: assumedSeerRating <- ${mappedNumeric}`);
      continue;
    }

    // Pattern examples:
    // "If <field> is "I don't know", then assume <targetField> = ..."
    // "If <field> is "I don't know", then assume <targetField>:"
    const ifUnknownMatch = instruction.match(
      /if\s+(.+?)\s+is\s+["']?i\s*don[’']?t\s*know["']?\s*,?\s*then\s+assume\s+(.+?)\s*(?:=|:)/i,
    );

    if (ifUnknownMatch) {
      const sourceFieldText = ifUnknownMatch[1];
      const targetFieldText = ifUnknownMatch[2];
      const sourceKey = resolveFieldFromText(sourceFieldText, aliasMap, step.key);
      const targetKey = resolveFieldFromText(targetFieldText, aliasMap, step.key);

      if (sourceKey && targetKey && isUnknown(nextLead[sourceKey])) {
        const mappedValue = mappedNumeric !== null ? mappedNumeric : mappedValueRaw || selectedValue;
        if (mappedValue !== undefined && mappedValue !== null && String(mappedValue).trim() !== "") {
          nextLead[targetKey] = mappedValue;
          appliedRules.push(
            `${step.key}: ${targetKey} <- ${String(mappedValue)} (because ${sourceKey} is unknown)`,
          );
        }
      }
      continue;
    }

    // Pattern examples:
    // If desiredFuelType is "I don't know", desiredFuelType = currentFuelType.
    // If desiredFuelType is "I don't know" AND If currentFuelType is also "I don't know", currentFuelType = Gas and desiredFuelType = Gas
    const unknownThenAssignMatch = instruction.match(
      /if\s+(.+?)\s+is\s+["']?i\s*don[’']?t\s*know["']?\s*(?:and\s+if\s+(.+?)\s+is\s+also\s+["']?i\s*don[’']?t\s*know["']?)?\s*,\s*(.+)$/i,
    );

    if (unknownThenAssignMatch) {
      const cond1FieldText = unknownThenAssignMatch[1];
      const cond2FieldText = unknownThenAssignMatch[2];
      const assignmentsText = unknownThenAssignMatch[3];

      const cond1Key = resolveFieldFromText(cond1FieldText, aliasMap, step.key);
      const cond2Key = cond2FieldText
        ? resolveFieldFromText(cond2FieldText, aliasMap, step.key)
        : undefined;

      const cond1Ok = cond1Key ? isUnknown(nextLead[cond1Key]) : false;
      const cond2Ok = cond2Key ? isUnknown(nextLead[cond2Key]) : true;

      if (cond1Ok && cond2Ok) {
        const assignments = parseAssignments(assignmentsText);

        for (const assignment of assignments) {
          const targetKey = resolveFieldFromText(assignment.targetText, aliasMap, step.key);
          if (!targetKey) continue;

          const sourceKey = resolveFieldFromText(assignment.sourceText, aliasMap, step.key);
          const rhsValue = sourceKey ? nextLead[sourceKey] : assignment.sourceText;
          const numericRhs = toNumberIfNumeric(rhsValue);
          const finalValue = numericRhs !== null ? numericRhs : rhsValue;

          if (
            finalValue !== undefined &&
            finalValue !== null &&
            String(finalValue).trim() !== ""
          ) {
            nextLead[targetKey] = finalValue;
            appliedRules.push(
              `${step.key}: ${targetKey} <- ${String(finalValue)} (from conditional assignment)`,
            );
          }
        }
      }

      continue;
    }

    // Pattern: "Response to appear on estimate output next to <field> "... XXXXX /Own""
    const responseMatch = instruction.match(/response to appear on estimate output next to\s+([a-z0-9_]+)/i);
    if (responseMatch) {
      const refField = responseMatch[1];
      const refKey = resolveFieldFromText(refField, aliasMap, step.key) ?? refField;
      const base = String(nextLead[refKey] ?? "").trim();
      const option = String(selectedValue).trim();
      displayNotes[step.key] = base ? `${base} / ${option}` : option;
      appliedRules.push(`${step.key}: display note -> ${displayNotes[step.key]}`);
    }
  }

  return {
    lead: nextLead,
    appliedRules,
    displayNotes,
  };
}
